import { Check, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { normalizeId, normalizeText } from "@/lib/normalize";
import type { MultiSelectItem } from "@/types/multi-select";

type MultiSelectAutogrowProps = {
  id: string;
  label: string;
  helperText?: string;
  placeholder?: string;
  options: MultiSelectItem[];
  value: MultiSelectItem[];
  onChange: (next: MultiSelectItem[]) => void;
  allowCustomValue?: boolean;
  selectedItemsOnTop?: boolean;
  keepFilterOnSelect?: boolean;
  autoOpenOnFocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  maxVisibleOptions?: number;
  emptyMessage?: string;
};

const dedupeByLabel = (items: MultiSelectItem[]): MultiSelectItem[] => {
  const seen = new Set<string>();
  const deduped: MultiSelectItem[] = [];

  for (const item of items) {
    const normalized = normalizeText(item.label);
    if (seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    deduped.push({ ...item, id: item.id || normalizeId(item.label) });
  }

  return deduped;
};

export function MultiSelectAutogrow({
  id,
  label,
  helperText,
  placeholder,
  options,
  value,
  onChange,
  allowCustomValue = true,
  selectedItemsOnTop = true,
  keepFilterOnSelect = false,
  autoOpenOnFocus = true,
  disabled = false,
  readOnly = false,
  maxVisibleOptions = 8,
  emptyMessage = "No matches",
}: MultiSelectAutogrowProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = `${id}-listbox`;

  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [focusedChipIndex, setFocusedChipIndex] = useState<number | null>(null);

  const selectedItems = useMemo(() => dedupeByLabel(value), [value]);
  const selectedLookup = useMemo(
    () => new Set(selectedItems.map(item => normalizeText(item.label))),
    [selectedItems],
  );

  const mergedOptions = useMemo(() => dedupeByLabel([...options, ...selectedItems]), [options, selectedItems]);

  const filteredOptions = useMemo(() => {
    const normalizedFilter = normalizeText(filter);

    const matched = normalizedFilter
      ? mergedOptions.filter(item => normalizeText(item.label).includes(normalizedFilter))
      : mergedOptions;

    if (!selectedItemsOnTop) {
      return matched;
    }

    const selected: MultiSelectItem[] = [];
    const unselected: MultiSelectItem[] = [];

    for (const item of matched) {
      if (selectedLookup.has(normalizeText(item.label))) {
        selected.push(item);
      } else {
        unselected.push(item);
      }
    }

    return [...selected, ...unselected];
  }, [filter, mergedOptions, selectedItemsOnTop, selectedLookup]);

  const normalizedFilter = normalizeText(filter);
  const exactMatch = normalizedFilter.length
    ? mergedOptions.some(item => normalizeText(item.label) === normalizedFilter)
    : false;
  const canAddCustom = allowCustomValue && !readOnly && normalizedFilter.length > 0 && !exactMatch;
  const totalRows = filteredOptions.length + (canAddCustom ? 1 : 0);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveIndex(0);
        setFocusedChipIndex(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setActiveIndex(index => {
      if (totalRows === 0) {
        return 0;
      }
      return Math.min(index, totalRows - 1);
    });
  }, [isOpen, totalRows]);

  const applySelection = (item: MultiSelectItem) => {
    if (disabled || readOnly) {
      return;
    }

    const normalized = normalizeText(item.label);
    const exists = selectedItems.some(valueItem => normalizeText(valueItem.label) === normalized);

    const next = exists
      ? selectedItems.filter(valueItem => normalizeText(valueItem.label) !== normalized)
      : [...selectedItems, item];

    onChange(next);
    setFocusedChipIndex(null);

    if (!keepFilterOnSelect) {
      setFilter("");
    }

    setIsOpen(true);
  };

  const addCustomValue = () => {
    if (!canAddCustom) {
      return;
    }

    const labelValue = filter.trim();
    if (!labelValue) {
      return;
    }

    applySelection({
      id: normalizeId(labelValue),
      label: labelValue,
      source: "custom",
    });
  };

  const removeByIndex = (index: number) => {
    if (disabled || readOnly) {
      return;
    }

    const item = selectedItems[index];
    if (!item) {
      return;
    }

    const normalized = normalizeText(item.label);
    onChange(selectedItems.filter(valueItem => normalizeText(valueItem.label) !== normalized));
    setFocusedChipIndex(previous => {
      if (previous === null) {
        return null;
      }
      if (previous >= index) {
        return Math.max(0, previous - 1);
      }
      return previous;
    });
  };

  return (
    <div className="space-y-3">
      <label htmlFor={id} className="pointer-events-none block text-lg text-[var(--color-text-primary)]">
        {label}
      </label>
      {helperText && <p className="text-sm text-[var(--color-text-tertiary)]">{helperText}</p>}

      <div className="relative" ref={wrapperRef}>
        <div
          className={`min-h-11 w-full rounded-lg border-2 bg-[var(--color-bg-input)] px-2 py-1 transition-colors duration-[var(--transition-base)] ${
            disabled
              ? "cursor-not-allowed opacity-60"
              : "border-transparent focus-within:border-[var(--color-border-focus)]"
          }`}
          onMouseDown={event => {
            if (disabled) {
              return;
            }
            event.preventDefault();
            setIsOpen(true);
            inputRef.current?.focus();
          }}
        >
          <div className="flex flex-wrap items-center gap-2">
            {selectedItems.map((item, index) => {
              const focused = focusedChipIndex === index;
              return (
                <span
                  key={`${item.id}-${index}`}
                  className={`inline-flex max-w-full items-center gap-1 rounded-full border px-3 py-1 text-sm ${
                    focused
                      ? "border-[var(--color-border-focus)] bg-[var(--color-bg-active)] text-[var(--color-text-primary)]"
                      : "border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]"
                  }`}
                >
                  <span className="break-words">{item.label}</span>
                  <button
                    type="button"
                    disabled={disabled || readOnly}
                    onClick={event => {
                      event.stopPropagation();
                      removeByIndex(index);
                    }}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[var(--color-text-secondary)] transition-colors duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed"
                    aria-label={`Remove ${item.label}`}
                  >
                    <X size={14} />
                  </button>
                </span>
              );
            })}

            <input
              id={id}
              ref={inputRef}
              type="text"
              role="combobox"
              aria-controls={listboxId}
              aria-expanded={isOpen}
              aria-autocomplete="list"
              aria-activedescendant={isOpen && totalRows > 0 ? `${id}-option-${activeIndex}` : undefined}
              disabled={disabled}
              readOnly={readOnly}
              value={filter}
              placeholder={selectedItems.length === 0 ? placeholder : undefined}
              onFocus={() => {
                setFocusedChipIndex(null);
                if (autoOpenOnFocus) {
                  setIsOpen(true);
                }
              }}
              onChange={event => {
                setFilter(event.target.value);
                setIsOpen(true);
                setFocusedChipIndex(null);
                setActiveIndex(0);
              }}
              onKeyDown={event => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  if (!isOpen) {
                    setIsOpen(true);
                    return;
                  }
                  if (totalRows > 0) {
                    setActiveIndex(previous => (previous + 1) % totalRows);
                  }
                  return;
                }

                if (event.key === "ArrowUp") {
                  event.preventDefault();
                  if (!isOpen) {
                    setIsOpen(true);
                    return;
                  }
                  if (totalRows > 0) {
                    setActiveIndex(previous => (previous - 1 + totalRows) % totalRows);
                  }
                  return;
                }

                if (event.key === "Enter") {
                  if (!isOpen) {
                    setIsOpen(true);
                    return;
                  }

                  event.preventDefault();

                  if (activeIndex < filteredOptions.length) {
                    const selectedOption = filteredOptions[activeIndex];
                    if (selectedOption) {
                      applySelection(selectedOption);
                    }
                    return;
                  }

                  if (canAddCustom) {
                    addCustomValue();
                  }
                  return;
                }

                if (event.key === "Escape") {
                  setIsOpen(false);
                  setFocusedChipIndex(null);
                  return;
                }

                if (event.key === "Backspace" && filter.length === 0) {
                  if (focusedChipIndex === null && selectedItems.length > 0) {
                    event.preventDefault();
                    setFocusedChipIndex(selectedItems.length - 1);
                    return;
                  }

                  if (focusedChipIndex !== null) {
                    event.preventDefault();
                    removeByIndex(focusedChipIndex);
                  }
                  return;
                }

                if (event.key === "Tab") {
                  setIsOpen(false);
                  setFocusedChipIndex(null);
                }

                setFocusedChipIndex(null);
              }}
              style={{ outline: "none" }}
              className="h-9 min-w-[180px] flex-1 bg-transparent px-2 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)] focus:outline-none focus-visible:outline-none"
            />
          </div>
        </div>

        {isOpen && (
          <div
            className="absolute z-20 mt-2 w-full overflow-hidden rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] shadow-lg"
            role="presentation"
          >
            <ul
              id={listboxId}
              role="listbox"
              aria-multiselectable="true"
              className="overflow-y-auto py-1"
              style={{ maxHeight: `${maxVisibleOptions * 2.5}rem` }}
            >
              {filteredOptions.map((option, index) => {
                const isActive = activeIndex === index;
                const isSelected = selectedLookup.has(normalizeText(option.label));

                return (
                  <li
                    id={`${id}-option-${index}`}
                    key={option.id}
                    role="option"
                    aria-selected={isSelected}
                    className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm ${
                      isActive
                        ? "bg-[var(--color-bg-active)] text-[var(--color-text-primary)]"
                        : "text-[var(--color-text-secondary)]"
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => applySelection(option)}
                  >
                    <span className="break-words">{option.label}</span>
                    {isSelected && <Check size={16} className="shrink-0" />}
                  </li>
                );
              })}

              {canAddCustom && (
                <li
                  id={`${id}-option-${filteredOptions.length}`}
                  role="option"
                  aria-selected={false}
                  className={`cursor-pointer px-3 py-2 text-sm ${
                    activeIndex === filteredOptions.length
                      ? "bg-[var(--color-bg-active)] text-[var(--color-text-primary)]"
                      : "text-[var(--color-text-secondary)]"
                  }`}
                  onMouseEnter={() => setActiveIndex(filteredOptions.length)}
                  onMouseDown={event => event.preventDefault()}
                  onClick={addCustomValue}
                >
                  Add "{filter.trim()}"
                </li>
              )}

              {filteredOptions.length === 0 && !canAddCustom && (
                <li className="px-3 py-2 text-sm text-[var(--color-text-tertiary)]">{emptyMessage}</li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
